/**
 * Hook pour gérer les features accessibles selon le plan d'abonnement
 * Utilisé pour afficher/masquer les sections du dashboard
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionsAPI } from '../utils/api';

// Types de plans supportés
export type PlanCode = 'TRIAL' | 'STARTER' | 'PRO' | 'ENTERPRISE' | 'FREE';

// Features du dashboard
export interface DashboardFeatures {
  // Section KPIs essentiels - tous les plans
  essentialKpis: boolean;
  recentActivity: boolean;

  // Section graphique évolution - STARTER+
  revenueChart: boolean;
  revenueChartMonths: 3 | 6 | 12;

  // Section performance commerciale - PRO+
  commercialPerformance: boolean;

  // Section MRR - PRO+
  recurringRevenue: boolean;

  // Section Produits & Catégories - PRO+
  productsAnalytics: boolean;

  // Section AI Insights - ENTERPRISE
  aiInsights: boolean;
  healthScore: boolean;

  // Sélecteur de période
  periodSelector: boolean;
  availablePeriods: ('7d' | '30d' | '90d' | '365d')[];
}

// Configuration des features par plan
const PLAN_FEATURES: Record<PlanCode, DashboardFeatures> = {
  FREE: {
    essentialKpis: true,
    recentActivity: true,
    revenueChart: false,
    revenueChartMonths: 3,
    commercialPerformance: false,
    recurringRevenue: false,
    productsAnalytics: false,
    aiInsights: false,
    healthScore: false,
    periodSelector: false,
    availablePeriods: ['30d'],
  },
  TRIAL: {
    // Trial a accès à tout pour 14 jours (comme ENTERPRISE)
    essentialKpis: true,
    recentActivity: true,
    revenueChart: true,
    revenueChartMonths: 12,
    commercialPerformance: true,
    recurringRevenue: true,
    productsAnalytics: true,
    aiInsights: true,
    healthScore: true,
    periodSelector: true,
    availablePeriods: ['7d', '30d', '90d', '365d'],
  },
  STARTER: {
    essentialKpis: true,
    recentActivity: true,
    revenueChart: true,
    revenueChartMonths: 3,
    commercialPerformance: false,
    recurringRevenue: false,
    productsAnalytics: false,
    aiInsights: false,
    healthScore: false,
    periodSelector: true,
    availablePeriods: ['7d', '30d'],
  },
  PRO: {
    essentialKpis: true,
    recentActivity: true,
    revenueChart: true,
    revenueChartMonths: 6,
    commercialPerformance: true,
    recurringRevenue: true,
    productsAnalytics: true,
    aiInsights: false,
    healthScore: false,
    periodSelector: true,
    availablePeriods: ['7d', '30d', '90d'],
  },
  ENTERPRISE: {
    essentialKpis: true,
    recentActivity: true,
    revenueChart: true,
    revenueChartMonths: 12,
    commercialPerformance: true,
    recurringRevenue: true,
    productsAnalytics: true,
    aiInsights: true,
    healthScore: true,
    periodSelector: true,
    availablePeriods: ['7d', '30d', '90d', '365d'],
  },
};

// Normaliser le code du plan
function normalizePlanCode(planId: string | undefined): PlanCode {
  if (!planId) return 'FREE';

  const normalized = planId
    .replace(/^plan[-_]/i, '')
    .toUpperCase()
    .trim();

  if (normalized === 'TRIAL') return 'TRIAL';
  if (normalized === 'STARTER' || normalized === 'SOLO') return 'STARTER';
  if (normalized === 'PRO' || normalized === 'PROFESSIONAL') return 'PRO';
  if (normalized === 'ENTERPRISE' || normalized === 'BUSINESS') return 'ENTERPRISE';

  return 'FREE';
}

export interface UsePlanFeaturesResult {
  // État
  loading: boolean;
  error: string | null;

  // Plan actuel
  planCode: PlanCode;
  planName: string;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;

  // Features
  features: DashboardFeatures;

  // Helpers
  hasFeature: (feature: keyof DashboardFeatures) => boolean;
  canUpgrade: boolean;
  upgradeUrl: string;
}

export function usePlanFeatures(): UsePlanFeaturesResult {
  // Utiliser React Query avec le même cache que useTrialGuard
  const { data: subscription, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: () => subscriptionsAPI.getCurrent(),
    staleTime: 30 * 1000, // Cache 30 secondes
    retry: 1,
    refetchOnWindowFocus: true,
  });

  const error = queryError ? (queryError as Error).message : null;

  // Calculer le plan actuel
  const planCode = useMemo(() => {
    return normalizePlanCode(subscription?.planId);
  }, [subscription?.planId]);

  // Vérifier si trial actif
  const isTrialActive = useMemo(() => {
    if (!subscription?.trialEndsAt) return false;
    return new Date(subscription.trialEndsAt) > new Date();
  }, [subscription?.trialEndsAt]);

  // Jours restants du trial
  const trialDaysRemaining = useMemo(() => {
    if (!subscription?.trialEndsAt) return null;
    const trialEnd = new Date(subscription.trialEndsAt);
    const now = new Date();
    if (trialEnd <= now) return 0;
    return Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }, [subscription?.trialEndsAt]);

  // Obtenir les features pour ce plan
  const features = useMemo(() => {
    // Si trial actif, utiliser les features TRIAL (accès complet)
    if (isTrialActive && planCode === 'TRIAL') {
      return PLAN_FEATURES.TRIAL;
    }
    return PLAN_FEATURES[planCode] || PLAN_FEATURES.FREE;
  }, [planCode, isTrialActive]);

  // Helper pour vérifier une feature
  const hasFeature = (feature: keyof DashboardFeatures): boolean => {
    const value = features[feature];
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  };

  // Nom du plan pour affichage
  const planName = useMemo(() => {
    const names: Record<PlanCode, string> = {
      FREE: 'Gratuit',
      TRIAL: 'Essai gratuit',
      STARTER: 'Starter',
      PRO: 'Pro',
      ENTERPRISE: 'Enterprise',
    };
    return names[planCode];
  }, [planCode]);

  // Peut upgrade ?
  const canUpgrade = planCode !== 'ENTERPRISE';
  const upgradeUrl = '/pricing';

  return {
    loading,
    error,
    planCode,
    planName,
    isTrialActive,
    trialDaysRemaining,
    features,
    hasFeature,
    canUpgrade,
    upgradeUrl,
  };
}

// Export des constantes pour réutilisation
export { PLAN_FEATURES };
